import React, { useState, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL;

// ── Un único plan, precio plano. Debe coincidir con satfire-backend/src/services/pricing.js
const PRECIO_MENSUAL = 13900;      // CLP, IVA incluido
const MAX_HA_SELF_SERVE = 8;       // sobre esto: contáctanos / plan Empresa

// ── Plan Comunal — debe coincidir con satfire-backend/src/services/pricing.js
// (TIERS_COMUNAL, MIN_PARCELAS_COMUNAL, MAX_M2_COMUNAL). La banda real la calcula el
// backend al momento de pagar según cuántos contactos haya cargados — esto es solo para
// mostrar el precio en pantalla mientras el comité arma la lista de vecinos.
const MIN_PARCELAS_COMUNAL = 5;
const MAX_HA_COMUNAL = 50;
const TIERS_COMUNAL = [
  { id: 'plan-comunal-8',  nombre: 'Plan Comunal 8',  min: 5,  max: 8,  monto: 79900 },
  { id: 'plan-comunal-15', nombre: 'Plan Comunal 15', min: 9,  max: 15, monto: 109900 },
  { id: 'plan-comunal-25', nombre: 'Plan Comunal 25', min: 16, max: 25, monto: 149900 },
];
function tierPorViviendas(n) {
  return TIERS_COMUNAL.find(t => n >= t.min && n <= t.max) || null;
}

// Flow activó el contrato de cobro automático el 2026-09-16 y se validó end-to-end con una
// suscripción de prueba real (customer/register + subscription/create). El checkout ahora
// usa /suscribir-mensual (registro de tarjeta + suscripción con renovación automática real).
const RENOVACION_AUTO_DISPONIBLE = true;

function formatPrecio(n) {
  return '$' + n.toLocaleString('es-CL');
}
function fmtHa(m2) {
  return `${(m2 / 10000).toLocaleString('es-CL', { maximumFractionDigits: 1 })} ha`;
}

const INCLUYE = [
  'Alertas por WhatsApp en tiempo real',
  'Hasta 3 números de contacto',
  'Detección satelital cada 15 min',
  'Ubicación exacta del foco y nivel de riesgo',
  'Números de emergencia en cada alerta',
];

export default function PantallaPago({ usuario, token, predio, onVolver, onExito, dm }) {
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  const esComunal = usuario?.tipo_cuenta === 'comunal';
  const maxHa = esComunal ? MAX_HA_COMUNAL : MAX_HA_SELF_SERVE;

  // ── Plan Comunal: lista de vecinos (predio_contactos) ──────────────────────────────
  const [contactos, setContactos] = useState([]);
  const [cargandoContactos, setCargandoContactos] = useState(esComunal);
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoTelefono, setNuevoTelefono] = useState('');
  const [agregando, setAgregando] = useState(false);
  const [errorContacto, setErrorContacto] = useState('');

  const headers = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token };

  useEffect(() => {
    if (!esComunal || !predio?.id) return;
    fetch(`${API}/api/cliente/predios/${predio.id}/contactos`, { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json())
      .then(data => { if (data.ok) setContactos(data.contactos || []); })
      .catch(() => {})
      .finally(() => setCargandoContactos(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esComunal, predio?.id]);

  const totalViviendas = 1 + contactos.length; // +1 = el número principal del predio
  const tierActual = tierPorViviendas(totalViviendas);
  const faltanParaCalificar = Math.max(0, MIN_PARCELAS_COMUNAL - totalViviendas);
  const listoParaComunal = !esComunal || (tierActual != null);

  const agregarVecino = async () => {
    setErrorContacto('');
    if (!nuevoNombre.trim()) return setErrorContacto('Ingresa el nombre o la dirección de la vivienda.');
    const digits = nuevoTelefono.replace(/\D/g, '');
    if (digits.length < 9) return setErrorContacto('Teléfono inválido — ingresa el número completo con código de país.');
    setAgregando(true);
    try {
      const res = await fetch(`${API}/api/cliente/predios/${predio.id}/contactos`, {
        method: 'POST', headers,
        body: JSON.stringify({ nombre: nuevoNombre.trim(), telefono: nuevoTelefono.trim(), rol: 'vecino' }),
      });
      const data = await res.json();
      if (!data.ok) return setErrorContacto(data.error || 'No se pudo agregar');
      setContactos(prev => [...prev, data.contacto]);
      setNuevoNombre(''); setNuevoTelefono('');
    } catch { setErrorContacto('Error de conexión'); }
    finally { setAgregando(false); }
  };

  const quitarVecino = async (contactoId) => {
    try {
      const res = await fetch(`${API}/api/cliente/predios/${predio.id}/contactos/${contactoId}`, {
        method: 'DELETE', headers: { Authorization: 'Bearer ' + token },
      });
      const data = await res.json();
      if (data.ok) setContactos(prev => prev.filter(c => c.id !== contactoId));
    } catch { /* silencioso — no es crítico, el usuario puede reintentar */ }
  };

  const haPredio = predio?.superficie_m2 ? predio.superficie_m2 / 10000 : null;
  const superaAutoservicio = haPredio != null && haPredio > maxHa;

  // Modo prueba: invisible para clientes — solo aparece con ?prueba=1 en la URL. Valida el
  // circuito real de Flow (producción incluida) cobrando $1.000. Quitar una vez validado.
  const modoPrueba = new URLSearchParams(window.location.search).get('prueba') === '1';

  const handlePagar = async (usarPrueba = false) => {
    setCargando(true);
    setError('');
    try {
      const auto = RENOVACION_AUTO_DISPONIBLE || usarPrueba; // en prueba probamos el flujo real
      const url = auto ? `${API}/api/pagos/suscribir-mensual` : `${API}/api/pagos/suscribir`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({
          predio_id: predio.id,
          superficie_m2: predio.superficie_m2,
          plan_prueba: usarPrueba || undefined,
        }),
      });
      const data = await res.json();
      if (!data.ok) return setError(data.error || 'Error al procesar el pago');
      window.location.href = auto ? data.urlRegistro : data.urlPago;
    } catch (e) {
      setError('Error de conexión');
    } finally {
      setCargando(false);
    }
  };

  const card = dm ? dm.card : 'white';
  const text = dm ? dm.text : '#222';
  const muted = dm ? dm.muted : '#888';
  const border = dm ? dm.border : '#e0e0e0';
  const accent = dm ? dm.accent : '#3d6b2a';
  const accentLight = dm ? dm.accentLight : '#eef5e8';
  const bg = dm ? dm.bg : '#f0f2f5';

  return (
    <div style={{ minHeight: '100vh', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif', padding: 16 }}>
      <div style={{ background: card, borderRadius: 20, padding: '32px 28px', width: '100%', maxWidth: 420, boxShadow: '0 24px 64px rgba(0,0,0,0.15)', border: '1px solid ' + border }}>

        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <img src="/favicon.png" alt="Montaraz" style={{ height: 56, marginBottom: 12 }} />
          <div style={{ fontSize: 20, fontWeight: 700, color: text }}>Activar alertas</div>
          <div style={{ fontSize: 13, color: muted, marginTop: 4 }}>
            {esComunal ? 'Plan Comunal — cancela cuando quieras' : 'Plan Mensual — cancela cuando quieras'}
          </div>
        </div>

        {/* Resumen del predio */}
        <div style={{ background: accentLight, borderRadius: 12, padding: '14px 16px', marginBottom: 16, border: '1px solid ' + border }}>
          <div style={{ fontSize: 11, color: muted, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>
            {esComunal ? 'Tu comunidad' : 'Tu predio'}
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: text }}>{predio?.nombre}</div>
          <div style={{ fontSize: 12, color: muted, marginTop: 2 }}>
            {predio?.superficie_m2 ? fmtHa(predio.superficie_m2) : 'Superficie no declarada'}
          </div>
        </div>

        {superaAutoservicio ? (
          <div style={{ background: accentLight, border: '1px solid ' + accent, borderRadius: 12, padding: '18px', marginBottom: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: accent, marginBottom: 8 }}>Tu {esComunal ? 'zona' : 'propiedad'} supera las {maxHa} ha</div>
            <div style={{ fontSize: 13, color: text, lineHeight: 1.6 }}>
              {esComunal ? 'Una comunidad de este tamaño necesita' : 'Una propiedad de este tamaño necesita'} varias zonas de monitoreo. Escríbenos a{' '}
              <a href="mailto:contacto@montaraz.cl" style={{ color: accent, fontWeight: 600 }}>contacto@montaraz.cl</a>{' '}
              y te preparamos un plan a medida.
            </div>
          </div>
        ) : (
          <>
            {esComunal && (
              <div style={{ border: '1.5px solid ' + border, borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: text }}>Viviendas de tu comunidad</div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: accent }}>{totalViviendas}</div>
                </div>
                <div style={{ fontSize: 11, color: muted, marginBottom: 10, lineHeight: 1.5 }}>
                  {faltanParaCalificar > 0
                    ? `Necesitas al menos ${MIN_PARCELAS_COMUNAL} viviendas para activar el Plan Comunal — agrega ${faltanParaCalificar} más.`
                    : `Con ${totalViviendas} viviendas te corresponde ${tierActual.nombre} — ${formatPrecio(tierActual.monto)}/mes en total.`}
                </div>

                {cargandoContactos ? (
                  <div style={{ fontSize: 12, color: muted, textAlign: 'center', padding: '8px 0' }}>Cargando vecinos...</div>
                ) : (
                  <>
                    {contactos.length > 0 && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 11, color: muted, marginBottom: 4 }}>
                          Vivienda 1: {predio?.telefono || 'la tuya'} (ya incluida)
                        </div>
                        {contactos.map((c, i) => (
                          <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11.5, color: text, padding: '5px 0', borderTop: '1px solid ' + border }}>
                            <span>Vivienda {i + 2}: {c.nombre} · {c.telefono}</span>
                            <button onClick={() => quitarVecino(c.id)} style={{ background: 'none', border: 'none', color: muted, cursor: 'pointer', fontSize: 13, padding: '0 2px' }} title="Quitar" aria-label={`Quitar a ${c.nombre}`}>×</button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                      <input
                        placeholder="Nombre o dirección"
                        value={nuevoNombre}
                        onChange={e => setNuevoNombre(e.target.value)}
                        style={{ flex: 1, minWidth: 0, padding: '8px 10px', borderRadius: 8, border: '1px solid ' + border, fontSize: 12, background: card, color: text, fontFamily: 'Inter, sans-serif' }}
                      />
                      <input
                        placeholder="+56 9 1234 5678"
                        value={nuevoTelefono}
                        onChange={e => setNuevoTelefono(e.target.value)}
                        style={{ width: 128, padding: '8px 10px', borderRadius: 8, border: '1px solid ' + border, fontSize: 12, background: card, color: text, fontFamily: 'Inter, sans-serif' }}
                      />
                    </div>
                    {errorContacto && <div style={{ fontSize: 11, color: '#c53030', marginBottom: 6 }}>{errorContacto}</div>}
                    <button
                      onClick={agregarVecino}
                      disabled={agregando || contactos.length >= 24}
                      style={{ width: '100%', padding: '7px', background: 'none', border: '1.5px dashed ' + accent, borderRadius: 8, color: accent, fontSize: 12, fontWeight: 600, cursor: agregando ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif' }}
                    >
                      {agregando ? 'Agregando...' : contactos.length >= 24 ? 'Máximo alcanzado (25 viviendas)' : '+ Agregar vivienda'}
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Plan y precio */}
            <div style={{ border: '2px solid ' + accent, borderRadius: 12, padding: '18px', marginBottom: 16, opacity: (esComunal && !listoParaComunal) ? 0.55 : 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: accent }}>{esComunal ? (tierActual?.nombre || 'Plan Comunal') : 'Plan Mensual'}</div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: 24, fontWeight: 800, color: accent }}>
                    {formatPrecio(esComunal ? (tierActual?.monto || TIERS_COMUNAL[0].monto) : PRECIO_MENSUAL)}
                  </span>
                  <span style={{ fontSize: 13, color: muted }}>/mes</span>
                </div>
              </div>
              <div style={{ fontSize: 10.5, color: muted, textAlign: 'right', marginTop: 2 }}>IVA incluido{esComunal ? ' · total de la comunidad' : ''}</div>

              <div style={{ borderTop: '1px solid ' + border, marginTop: 14, paddingTop: 12 }}>
                {(esComunal
                  ? [...INCLUYE.slice(0, 1), 'Hasta 25 números de contacto (uno por vivienda)', ...INCLUYE.slice(2)]
                  : INCLUYE
                ).map(f => (
                  <div key={f} style={{ fontSize: 11.5, color: text, marginBottom: 5 }}>✓ {f}</div>
                ))}
              </div>
            </div>

            <div style={{ background: '#fff8e8', border: '1px solid #d69e2e', borderRadius: 8, padding: '10px 12px', fontSize: 11, color: '#8b6914', marginBottom: 16 }}>
              🔒 {RENOVACION_AUTO_DISPONIBLE
                ? `Registras tu tarjeta con Flow de forma segura. Se cobra ${formatPrecio(esComunal ? (tierActual?.monto || TIERS_COMUNAL[0].monto) : PRECIO_MENSUAL)} cada mes y cancelas la renovación cuando quieras desde tu perfil.`
                : 'Pago seguro procesado por Flow. Cubre 1 mes — te avisamos antes de que venza para renovar.'}
            </div>

            {error && (
              <div style={{ background: '#fff0f0', color: '#c53030', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12, border: '1px solid #feb2b2' }}>
                {error}
              </div>
            )}

            <button onClick={() => handlePagar(false)} disabled={cargando || (esComunal && !listoParaComunal)} style={{
              width: '100%', padding: '14px', background: accent, color: 'white',
              border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700,
              cursor: (cargando || (esComunal && !listoParaComunal)) ? 'not-allowed' : 'pointer', opacity: (cargando || (esComunal && !listoParaComunal)) ? 0.6 : 1,
              fontFamily: 'Inter, sans-serif', marginBottom: 10,
            }}>
              {cargando
                ? 'Procesando...'
                : esComunal && !listoParaComunal
                  ? `Agrega ${faltanParaCalificar} vivienda${faltanParaCalificar === 1 ? '' : 's'} más para continuar`
                  : RENOVACION_AUTO_DISPONIBLE
                    ? `Suscribirme — ${formatPrecio(esComunal ? tierActual.monto : PRECIO_MENSUAL)}/mes`
                    : `Pagar ${formatPrecio(PRECIO_MENSUAL)}`}
            </button>

            {modoPrueba && (
              <button onClick={() => handlePagar(true)} disabled={cargando} style={{
                width: '100%', padding: '10px', background: 'none', color: '#c05621',
                border: '1.5px dashed #c05621', borderRadius: 10, fontSize: 12, fontWeight: 600,
                cursor: cargando ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif', marginBottom: 10,
              }}>
                🧪 Probar suscripción real por $1.000/mes
              </button>
            )}
          </>
        )}

        <button onClick={onVolver} style={{ width: '100%', padding: '10px', background: 'none', border: 'none', color: muted, fontSize: 13, cursor: 'pointer' }}>
          Volver
        </button>
      </div>
    </div>
  );
}
